/**
 * Error catalog — the stable, machine-readable error codes shared by the REST
 * surface and the WebSocket protocol.
 *
 * Source of truth: `docs/prd/07-api-contract.md` §8 (REST envelope + HTTP→code
 * mapping, §8.2) and §8.3 (WS `error` frame codes), plus `05-realtime-infra.md`
 * §3.4 (WS close codes).
 */

/* -------------------------------------------------------------------------- */
/*  REST error codes (§8.2)                                                    */
/* -------------------------------------------------------------------------- */

export const REST_ERROR_CODES = {
  bad_request: 'bad_request',
  invalid_cursor: 'invalid_cursor',
  validation_error: 'validation_error',
  unauthorized: 'unauthorized',
  invalid_ticket: 'invalid_ticket',
  forbidden: 'forbidden',
  not_a_member: 'not_a_member',
  not_found: 'not_found',
  user_not_found: 'user_not_found',
  media_not_found: 'media_not_found',
  conflict: 'conflict',
  idempotency_conflict: 'idempotency_conflict',
  no_keys_published: 'no_keys_published',
  media_too_large: 'media_too_large',
  unprocessable: 'unprocessable',
  group_full: 'group_full',
  cannot_leave_direct: 'cannot_leave_direct',
  must_promote_admin_first: 'must_promote_admin_first',
  rate_limited: 'rate_limited',
  internal_error: 'internal_error',
  dependency_unavailable: 'dependency_unavailable',
} as const;

export type RestErrorCode = (typeof REST_ERROR_CODES)[keyof typeof REST_ERROR_CODES];

/** Canonical HTTP status for each REST error code (§8.2). */
export const REST_ERROR_HTTP_STATUS: Record<RestErrorCode, number> = {
  bad_request: 400,
  invalid_cursor: 400,
  validation_error: 400,
  unauthorized: 401,
  invalid_ticket: 401,
  forbidden: 403,
  not_a_member: 403,
  not_found: 404,
  user_not_found: 404,
  media_not_found: 404,
  conflict: 409,
  idempotency_conflict: 409,
  no_keys_published: 410,
  media_too_large: 413,
  unprocessable: 422,
  group_full: 422,
  cannot_leave_direct: 422,
  must_promote_admin_first: 422,
  rate_limited: 429,
  internal_error: 500,
  dependency_unavailable: 503,
};

/** Human-readable default messages (overridable per-call). */
export const REST_ERROR_DEFAULT_MESSAGE: Record<RestErrorCode, string> = {
  bad_request: 'Malformed request or missing required field.',
  invalid_cursor: 'Stale or tampered pagination cursor.',
  validation_error: 'One or more fields failed validation.',
  unauthorized: 'Missing, expired, or invalid access token.',
  invalid_ticket: 'WebSocket ticket is bad, expired, or replayed.',
  forbidden: 'You are not allowed to perform this action.',
  not_a_member: 'You are not a member of this conversation.',
  not_found: 'Resource not found.',
  user_not_found: 'No Sinyal account matches that handle, phone, or id.',
  media_not_found: 'Unknown media object.',
  conflict: 'The request conflicts with current state.',
  idempotency_conflict: 'Same Idempotency-Key was used with a different payload.',
  no_keys_published: 'The target user has never published prekeys.',
  media_too_large: 'The upload exceeds the maximum allowed size.',
  unprocessable: 'The request is semantically invalid.',
  group_full: 'This conversation has reached the 256-member cap.',
  cannot_leave_direct: 'A direct conversation cannot be left; mute or archive instead.',
  must_promote_admin_first: 'Promote another admin before removing the last admin.',
  rate_limited: 'Rate limit exceeded. Retry after the indicated delay.',
  internal_error: 'An unexpected server error occurred.',
  dependency_unavailable: 'A backing service is temporarily unavailable. Retry with backoff.',
};

/* -------------------------------------------------------------------------- */
/*  REST error envelope (§8.1)                                                 */
/* -------------------------------------------------------------------------- */

/** Optional field-level validation context (`details`). */
export interface FieldError {
  field: string;
  message: string;
}

export interface ApiErrorBody {
  code: RestErrorCode;
  message: string;
  /** Echoes `REST_ERROR_HTTP_STATUS[code]`; present for client convenience. */
  http_status: number;
  /** ULID request id; mirrors the `x-request-id` response header. */
  request_id: string;
  /** Optional structured context (e.g. `FieldError[]` for `validation_error`). */
  details: FieldError[] | Record<string, unknown> | null;
}

/** The full REST error response body. */
export interface ApiErrorResponse {
  error: ApiErrorBody;
}

/**
 * Build a uniform REST error envelope. `http_status` is derived from the code
 * so callers cannot drift the status from the catalog.
 */
export function buildApiError(args: {
  code: RestErrorCode;
  request_id: string;
  message?: string;
  details?: ApiErrorBody['details'];
}): ApiErrorResponse {
  return {
    error: {
      code: args.code,
      message: args.message ?? REST_ERROR_DEFAULT_MESSAGE[args.code],
      http_status: REST_ERROR_HTTP_STATUS[args.code],
      request_id: args.request_id,
      details: args.details ?? null,
    },
  };
}

/** Narrow an unknown response body to the REST error envelope shape. */
export function isApiErrorResponse(value: unknown): value is ApiErrorResponse {
  if (typeof value !== 'object' || value === null) return false;
  const err = (value as { error?: unknown }).error;
  if (typeof err !== 'object' || err === null) return false;
  const code = (err as { code?: unknown }).code;
  return typeof code === 'string' && code in REST_ERROR_CODES;
}

/* -------------------------------------------------------------------------- */
/*  WebSocket error-frame codes (§8.3)                                         */
/* -------------------------------------------------------------------------- */

export const WS_ERROR_CODES = {
  bad_frame: 'bad_frame',
  not_a_member: 'not_a_member',
  unknown_conversation: 'unknown_conversation',
  persist_failed: 'persist_failed',
  rate_limited: 'rate_limited',
  auth_expired: 'auth_expired',
  payload_too_large: 'payload_too_large',
  internal_error: 'internal_error',
} as const;

export type WsErrorCode = (typeof WS_ERROR_CODES)[keyof typeof WS_ERROR_CODES];

/** Whether re-sending the offending frame (same `client_msg_id`) is safe. */
export const WS_ERROR_RETRYABLE: Record<WsErrorCode, boolean> = {
  bad_frame: false,
  not_a_member: false,
  unknown_conversation: false,
  persist_failed: true,
  rate_limited: true,
  auth_expired: false,
  payload_too_large: false,
  internal_error: true,
};

export const WS_ERROR_DEFAULT_MESSAGE: Record<WsErrorCode, string> = {
  bad_frame: 'Malformed or unknown frame.',
  not_a_member: 'You are not a member of this conversation.',
  unknown_conversation: 'The referenced conversation does not exist.',
  persist_failed: 'The server could not durably store the message. Re-send the same client_msg_id.',
  rate_limited: 'You are sending frames too quickly. Back off and retry.',
  auth_expired: 'Socket auth expired. Send auth.refresh with a fresh ticket.',
  payload_too_large: 'Frame exceeds the 256 KB cap. Media must go through R2, not the socket.',
  internal_error: 'A transient server error occurred. Retry after backoff.',
};

/* -------------------------------------------------------------------------- */
/*  WebSocket close codes (05-realtime-infra §3.4)                             */
/* -------------------------------------------------------------------------- */

export const WS_CLOSE_CODES = {
  /** Normal client close. */
  NORMAL: 1000,
  /** Internal error — client should reconnect with backoff. */
  INTERNAL_ERROR: 1011,
  /** Malformed frame / protocol violation. */
  MALFORMED: 4400,
  /** Auth expired or invalid ticket. */
  AUTH_INVALID: 4401,
  /** Not a member of requested conversation. */
  NOT_A_MEMBER: 4403,
  /** Heartbeat timeout (no ping within 90s). */
  HEARTBEAT_TIMEOUT: 4408,
  /** Rate limited (connection-level flood). */
  RATE_LIMITED: 4429,
} as const;

export type WsCloseCode = (typeof WS_CLOSE_CODES)[keyof typeof WS_CLOSE_CODES];

/** Frame-size cap: a single WS text frame must be ≤ 256 KB (§8.3). */
export const WS_FRAME_MAX_BYTES = 256 * 1024;

/**
 * Build a typed WS `error` frame payload (minus the common envelope, which the
 * caller fills via `makeFrame`). `retryable` is sourced from the catalog so it
 * stays consistent across emitters.
 */
export function buildWsError(args: {
  ref_id: string;
  code: WsErrorCode;
  message?: string;
}): { ref_id: string; code: WsErrorCode; message: string; retryable: boolean } {
  return {
    ref_id: args.ref_id,
    code: args.code,
    message: args.message ?? WS_ERROR_DEFAULT_MESSAGE[args.code],
    retryable: WS_ERROR_RETRYABLE[args.code],
  };
}
